import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  FileText, 
  Download,
  Send,
  Calendar,
  TrendingUp,
  TrendingDown,
  Loader2,
  Mail,
  Eye,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeeklyReports = () => {
  const { organization } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/weekly-reports`);
      setReports(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendReportNow = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/weekly-report/send-now`);
      toast.success("Rapport envoyé par email avec succès !");
      fetchReports();
    } catch (error) {
      console.error("Error sending report:", error);
      toast.error("Erreur lors de l'envoi du rapport");
    } finally {
      setSending(false);
    }
  };

  const previewReport = async () => {
    setPreviewing(true);
    try {
      const response = await axios.get(`${API}/weekly-report/preview`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success("Aperçu du rapport généré !");
    } catch (error) {
      console.error("Error previewing report:", error);
      toast.error("Erreur lors de la génération de l'aperçu");
    } finally {
      setPreviewing(false);
    }
  };

  const downloadPdf = async (reportId) => {
    try {
      const response = await axios.get(`${API}/weekly-reports/${reportId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rapport_hebdo_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="weekly-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Rapports hebdomadaires
          </h1>
          <p className="text-slate-500 mt-1">
            Synthèses automatiques envoyées chaque dimanche soir
          </p>
        </div>
        
        <div className="flex gap-3">
          <Button 
            onClick={previewReport}
            disabled={previewing}
            variant="outline"
            className="rounded-full"
            data-testid="preview-report-btn"
          >
            {previewing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Génération...
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Aperçu
              </>
            )}
          </Button>
          <Button 
            onClick={sendReportNow}
            disabled={sending}
            className="rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90"
            data-testid="send-report-now-btn"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Envoyer maintenant
              </>
            )}
          </Button>
        </div>
      </div>

      {/* New Professional Report Banner */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-pink-500 rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="relative z-10 flex items-start gap-4">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-xl mb-2">Rapports PDF Professionnels</h3>
            <p className="text-white/80 text-sm leading-relaxed">
              Vos rapports incluent désormais des <strong>graphiques interactifs</strong>, 
              une <strong>analyse visuelle</strong> de la répartition des notes, 
              des <strong>cartes KPI colorées</strong> et un <strong>plan d'action IA détaillé</strong> avec priorités.
            </p>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl p-6 border border-violet-100">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Mail className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 mb-1">Email automatique chaque dimanche</h3>
            <p className="text-slate-600 text-sm">
              Vous recevez automatiquement un rapport PDF contenant le nombre d'avis positifs/négatifs, 
              la note moyenne, la progression et un plan d'action IA détaillé avec des estimations de coûts.
            </p>
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-600" />
            Historique des rapports
          </h2>
        </div>

        {reports.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-700 mb-2">Aucun rapport</h3>
            <p className="text-slate-500 text-sm mb-4">
              Votre premier rapport sera généré dimanche prochain ou cliquez sur "Envoyer maintenant"
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {reports.map((report) => (
              <div 
                key={report.id} 
                className="p-4 hover:bg-slate-50 transition-colors"
                data-testid={`report-item-${report.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">
                        Semaine du {formatDate(report.week_date)}
                      </h4>
                      <div className="flex items-center gap-4 mt-1 text-sm">
                        <span className="text-slate-500">
                          {report.stats?.week_total || 0} avis
                        </span>
                        <span className="flex items-center gap-1">
                          {(report.stats?.progression || 0) >= 0 ? (
                            <>
                              <TrendingUp className="w-4 h-4 text-emerald-500" />
                              <span className="text-emerald-600">
                                +{(report.stats?.progression || 0).toFixed(1)}
                              </span>
                            </>
                          ) : (
                            <>
                              <TrendingDown className="w-4 h-4 text-red-500" />
                              <span className="text-red-600">
                                {(report.stats?.progression || 0).toFixed(1)}
                              </span>
                            </>
                          )}
                        </span>
                        <span className="text-slate-500">
                          Note: {(report.stats?.week_avg || 0).toFixed(1)}/5
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadPdf(report.id)}
                    className="rounded-full"
                    data-testid={`download-pdf-${report.id}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyReports;
